/**
 * Template-first Cycle Builder: pick a saved template, then review its units.
 */
import React, { useMemo, useState } from "react";
import { translateUi } from "../i18n/index";
import { CheckCircle2, ChevronDown, ClipboardList, Clock, FileText, Lock } from "../icons";

export type OutlinePanelSaveState = "idle" | "saving" | "saved";
import { cb, cbCn } from "./cycleBuilderTheme";
import { formatCycleDurationLabel } from "./formatCycleDuration";
import {
  isTemplateCriteriaLineInCycle,
  isInternalLibraryElementId,
  stripReferenceForDisplay,
  uniqueDemonstrateAndExplain,
  type LibraryCycle,
  type LibraryUnit,
} from "./cycleLibraryUi";
import type { WizardCriteriaItem } from "./CycleWizardStore";

export function templateLibraryStats(cycle: LibraryCycle): { units: number; elements: number; criteria: number } {
  let elements = 0;
  let criteria = 0;
  for (const unit of cycle.units || []) {
    for (const el of unit.elements || []) {
      elements += 1;
      const unique = uniqueDemonstrateAndExplain(el.demonstrate, el.explain);
      criteria += unique.demonstrate.length + unique.explain.length;
    }
  }
  return { units: cycle.units?.length ?? 0, elements, criteria };
}

export function templateDurationMonths(cycle: LibraryCycle): number {
  const yearsRaw = Number(cycle.expiryYears ?? 2);
  const years = Number.isFinite(yearsRaw) && yearsRaw >= 1 && yearsRaw <= 10 ? Math.floor(yearsRaw) : 2;
  return years * 12;
}

function unitDisplayName(unit: LibraryUnit, index: number): { num: string; name: string } {
  const title = String(unit.title ?? "").trim();
  const titled = title.match(/^Unit\s+(\d+)\s*[–-]\s*(.+)$/i);
  if (titled) return { num: titled[1], name: titled[2] };
  const fromId = String(unit.id ?? "").match(/^u(\d+)$/i);
  if (fromId) {
    const rest = title.replace(/^unit\s*u?\d+\s*[-–:.]?\s*/i, "").trim() || title || `Unit ${fromId[1]}`;
    return { num: fromId[1], name: rest };
  }
  return { num: String(index + 1), name: title || String(unit.id ?? `Unit ${index + 1}`) };
}

const UNIT_TONES = [
  {
    bar: "bg-indigo-500",
    card: "border-indigo-200 bg-indigo-50/70",
    cardDark: "border-indigo-500/35 bg-indigo-950/40",
    badge: "bg-indigo-600 text-white",
    badgeDark: "bg-indigo-500 text-white",
    label: "text-indigo-800",
    labelDark: "text-indigo-200",
  },
  {
    bar: "bg-sky-500",
    card: "border-sky-200 bg-sky-50/70",
    cardDark: "border-sky-500/35 bg-sky-950/40",
    badge: "bg-sky-600 text-white",
    badgeDark: "bg-sky-500 text-white",
    label: "text-sky-800",
    labelDark: "text-sky-200",
  },
  {
    bar: "bg-violet-500",
    card: "border-violet-200 bg-violet-50/70",
    cardDark: "border-violet-500/35 bg-violet-950/40",
    badge: "bg-violet-600 text-white",
    badgeDark: "bg-violet-500 text-white",
    label: "text-violet-800",
    labelDark: "text-violet-200",
  },
  {
    bar: "bg-emerald-500",
    card: "border-emerald-200 bg-emerald-50/70",
    cardDark: "border-emerald-500/35 bg-emerald-950/40",
    badge: "bg-emerald-600 text-white",
    badgeDark: "bg-emerald-500 text-white",
    label: "text-emerald-800",
    labelDark: "text-emerald-200",
  },
  {
    bar: "bg-amber-500",
    card: "border-amber-200 bg-amber-50/80",
    cardDark: "border-amber-500/35 bg-amber-950/40",
    badge: "bg-amber-600 text-white",
    badgeDark: "bg-amber-500 text-white",
    label: "text-amber-900",
    labelDark: "text-amber-200",
  },
  {
    bar: "bg-teal-500",
    card: "border-teal-200 bg-teal-50/70",
    cardDark: "border-teal-500/35 bg-teal-950/40",
    badge: "bg-teal-600 text-white",
    badgeDark: "bg-teal-500 text-white",
    label: "text-teal-800",
    labelDark: "text-teal-200",
  },
] as const;

function collectTemplateCriteriaLines(
  el: LibraryUnit["elements"][number],
  cycleId: string,
  unitId: string,
  activeCriteriaItems?: WizardCriteriaItem[]
): { demonstrate: string[]; explain: string[] } {
  const demonstrate: string[] = [];
  const explain: string[] = [];
  (el.demonstrate || []).forEach((line, i) => {
    const raw = String(line ?? "").trim();
    if (!raw) return;
    if (!isTemplateCriteriaLineInCycle(cycleId, unitId, el.id, "demonstrate", i, raw, activeCriteriaItems)) return;
    demonstrate.push(raw);
  });
  (el.explain || []).forEach((line, i) => {
    const raw = String(line ?? "").trim();
    if (!raw) return;
    if (!isTemplateCriteriaLineInCycle(cycleId, unitId, el.id, "explain", i, raw, activeCriteriaItems)) return;
    explain.push(raw);
  });
  return { demonstrate, explain };
}

function elementInlineLabel(el: LibraryUnit["elements"][number]): string | null {
  const id = String(el.id ?? "").trim();
  if (isInternalLibraryElementId(id)) return null;
  return id;
}

function elementSectionTitle(el: LibraryUnit["elements"][number]): string | null {
  const id = String(el.id ?? "").trim();
  if (isInternalLibraryElementId(id)) {
    const name = String(el.title ?? "").trim();
    return name || null;
  }
  const elName = el.title.replace(/^element\s*[\d.]+\s*/i, "").trim() || el.title;
  if (!elName || (id && elName.toLowerCase() === id.toLowerCase())) return null;
  return elName;
}

function renderTemplateCriteriaRow(
  line: string,
  kind: "demonstrate" | "explain",
  elementLabel: string | null,
  key: string,
  isDarkMode: boolean
) {
  const isDemo = kind === "demonstrate";
  return (
    <li
      key={key}
      className={cbCn(
        "flex items-start gap-2 rounded-lg px-2.5 py-1.5 text-xs leading-snug",
        isDemo
          ? isDarkMode
            ? "bg-emerald-950/40 text-slate-200"
            : "bg-emerald-50 text-slate-700"
          : isDarkMode
            ? "bg-amber-950/40 text-slate-200"
            : "bg-amber-50 text-slate-700"
      )}
    >
      {elementLabel ? (
        <span
          className={cbCn(
            "mt-0.5 w-5 shrink-0 text-center text-[11px] font-bold tabular-nums",
            isDarkMode ? "text-slate-300" : "text-slate-600"
          )}
        >
          {elementLabel}
        </span>
      ) : null}
      <span
        className={cbCn(
          "mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full",
          isDemo ? (isDarkMode ? "bg-emerald-400" : "bg-emerald-500") : isDarkMode ? "bg-amber-400" : "bg-amber-500"
        )}
        aria-hidden
      />
      <span className="min-w-0 flex-1 break-words">{stripReferenceForDisplay(line)}</span>
    </li>
  );
}

function renderTemplateCriteriaLines(
  el: LibraryUnit["elements"][number],
  cycleId: string,
  unitId: string,
  isDarkMode: boolean,
  activeCriteriaItems?: WizardCriteriaItem[]
) {
  const { demonstrate, explain } = collectTemplateCriteriaLines(el, cycleId, unitId, activeCriteriaItems);
  if (demonstrate.length === 0 && explain.length === 0) return null;
  const elementLabel = elementInlineLabel(el);
  const sectionTitle = elementSectionTitle(el);

  return (
    <div key={el.id}>
      {sectionTitle ? (
        <p className={cbCn("mb-1 text-[11px] font-semibold", isDarkMode ? "text-slate-200" : "text-slate-800")}>{sectionTitle}</p>
      ) : null}
      <ul className="space-y-1">
        {demonstrate.map((line, i) => renderTemplateCriteriaRow(line, "demonstrate", elementLabel, `d-${el.id}-${i}`, isDarkMode))}
        {explain.map((line, i) => renderTemplateCriteriaRow(line, "explain", elementLabel, `e-${el.id}-${i}`, isDarkMode))}
      </ul>
    </div>
  );
}

function TemplateUnitSection({
  unit,
  unitIndex,
  cycleId,
  isDarkMode,
  activeCriteriaItems,
  collapsible = false,
}: {
  unit: LibraryUnit;
  unitIndex: number;
  cycleId: string;
  isDarkMode: boolean;
  activeCriteriaItems?: WizardCriteriaItem[];
  collapsible?: boolean;
}) {
  const [open, setOpen] = useState(!collapsible);
  const { num, name } = unitDisplayName(unit, unitIndex);
  const tone = UNIT_TONES[unitIndex % UNIT_TONES.length];
  const visibleElements = (unit.elements || [])
    .map((el) => {
      const lines = collectTemplateCriteriaLines(el, cycleId, unit.id, activeCriteriaItems);
      const count = lines.demonstrate.length + lines.explain.length;
      if (count === 0) return null;
      return { el, count };
    })
    .filter((x): x is { el: LibraryUnit["elements"][number]; count: number } => !!x);
  if (visibleElements.length === 0) return null;

  const unitCriteria = visibleElements.reduce((n, x) => n + x.count, 0);
  const summary = `${unitCriteria} ${translateUi("criteria")} · ${visibleElements.length} ${translateUi("elements")}`;

  const headerInner = (
    <>
      <span className={cbCn("w-1 shrink-0 self-stretch rounded-full", tone.bar)} aria-hidden />
      <span
        className={cbCn(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-bold tabular-nums",
          isDarkMode ? tone.badgeDark : tone.badge
        )}
      >
        {num}
      </span>
      <div className="min-w-0 flex-1">
        <h5 className={cbCn("text-sm font-semibold leading-snug break-words", isDarkMode ? "text-slate-50" : "text-slate-900")}>
          {name}
        </h5>
        <p className={cbCn("mt-0.5 text-[11px] font-medium", isDarkMode ? tone.labelDark : tone.label)}>{summary}</p>
      </div>
    </>
  );

  const body = (
    <div className={cbCn("space-y-3 px-3 pb-3 sm:px-4", isDarkMode ? "bg-slate-950/25" : "bg-white/70")}>
      {visibleElements.map(({ el }) => renderTemplateCriteriaLines(el, cycleId, unit.id, isDarkMode, activeCriteriaItems))}
    </div>
  );

  return (
    <section
      key={unit.id || `${unitIndex}`}
      className={cbCn("overflow-hidden rounded-2xl border", isDarkMode ? tone.cardDark : tone.card)}
    >
      {collapsible ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className={cbCn(
            "flex w-full items-start gap-3 px-3 py-3 text-left transition-colors sm:px-4",
            isDarkMode ? "hover:bg-slate-900/50" : "hover:bg-white/80"
          )}
        >
          <span
            className={cbCn(
              "mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-transform duration-200",
              open ? "rotate-0" : "-rotate-90",
              isDarkMode ? "text-slate-400" : "text-slate-500"
            )}
            aria-hidden
          >
            <ChevronDown className="h-4 w-4" />
          </span>
          {headerInner}
        </button>
      ) : (
        <header className="flex items-start gap-3 px-3 py-3 sm:px-4">{headerInner}</header>
      )}
      {!collapsible || open ? body : null}
    </section>
  );
}

export function TemplateUnitsCriteriaScroll({
  cycle,
  isDarkMode = false,
  isMobile = false,
  activeCriteriaItems,
  className,
  collapsibleUnits = false,
}: {
  cycle: LibraryCycle;
  isDarkMode?: boolean;
  isMobile?: boolean;
  /** When set, only lines still in the wizard criteria are shown (synced with left pane). */
  activeCriteriaItems?: WizardCriteriaItem[];
  className?: string;
  /** Preview modals: each unit starts collapsed until expanded. */
  collapsibleUnits?: boolean;
}) {
  const cycleId = String(cycle.id ?? "").trim();
  return (
    <div className={cbCn("min-h-0 flex-1 space-y-3 overflow-y-auto p-3 sm:p-4", isMobile ? "max-h-[min(48vh,420px)]" : "", className)}>
      {(cycle.units || []).map((unit, unitIndex) => (
        <TemplateUnitSection
          key={unit.id || `${unitIndex}`}
          unit={unit}
          unitIndex={unitIndex}
          cycleId={cycleId}
          isDarkMode={isDarkMode}
          activeCriteriaItems={activeCriteriaItems}
          collapsible={collapsibleUnits}
        />
      ))}
    </div>
  );
}

export function TemplatePickerPane({
  library,
  loading,
  selectedId,
  companyWizardContext,
  isFixed,
  onUseTemplate,
  isDarkMode = false,
  isMobile = false,
}: {
  library: LibraryCycle[];
  loading: boolean;
  selectedId?: string;
  companyWizardContext: boolean;
  isFixed: (cycle: LibraryCycle) => boolean;
  onUseTemplate: (cycle: LibraryCycle) => void;
  isDarkMode?: boolean;
  isMobile?: boolean;
}) {
  if (loading) {
    return (
      <div className={cbCn("rounded-2xl border px-5 py-16 text-center text-sm", isDarkMode ? "border-slate-700 bg-slate-900/80 text-slate-400" : "border-slate-200 bg-white text-slate-500")}>
        {translateUi("Loading templates…")}
      </div>
    );
  }

  if (library.length === 0) {
    return (
      <div className={cbCn("rounded-2xl border px-5 py-16 text-center", isDarkMode ? "border-slate-700 bg-slate-900/80" : "border-slate-200 bg-white")}>
        <p className={cbCn("font-semibold", isDarkMode ? "text-slate-100" : "text-slate-900")}>{translateUi("No templates yet")}</p>
        <p className={cbCn("mt-1 text-sm", cb.bodySm(isDarkMode))}>
          {translateUi("Saved competency templates will appear here.")}
        </p>
      </div>
    );
  }

  return (
    <div className={cbCn("grid gap-3", isMobile ? "grid-cols-1" : "sm:grid-cols-2")}>
      {library.map((cycle) => {
        const stats = templateLibraryStats(cycle);
        const duration = formatCycleDurationLabel(templateDurationMonths(cycle));
        const selected = String(selectedId ?? "").trim() === String(cycle.id).trim();
        const fixed = isFixed(cycle);
        return (
          <article
            key={cycle.id}
            className={cbCn(
              "flex flex-col rounded-2xl border p-4 shadow-sm transition-all duration-200 sm:p-5",
              selected
                ? isDarkMode
                  ? "border-indigo-400 bg-indigo-950/45 ring-1 ring-indigo-400/40"
                  : "border-indigo-500 bg-indigo-50/90 ring-1 ring-indigo-200"
                : isDarkMode
                  ? "border-slate-700 bg-slate-900/90 hover:border-indigo-500/40"
                  : "border-slate-200 bg-white hover:border-indigo-300 hover:shadow-md"
            )}
          >
            <div className="flex items-start gap-3">
              <span
                className={cbCn(
                  "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl",
                  selected
                    ? isDarkMode
                      ? "bg-indigo-500/30 text-indigo-200"
                      : "bg-indigo-100 text-indigo-700"
                    : isDarkMode
                      ? "bg-slate-800 text-slate-300"
                      : "bg-slate-100 text-slate-600"
                )}
                aria-hidden
              >
                {selected ? <CheckCircle2 className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
              </span>
              <div className="min-w-0 flex-1">
                <h4 className={cbCn("font-semibold leading-snug", isDarkMode ? "text-slate-50" : "text-slate-900")}>
                  {cycle.name}
                </h4>
                <p className={cbCn("mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs", cb.bodySm(isDarkMode))}>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" aria-hidden />
                    {duration}
                  </span>
                  <span aria-hidden>·</span>
                  <span>
                    {stats.units} {stats.units === 1 ? translateUi("unit") : translateUi("units")}
                  </span>
                  <span aria-hidden>·</span>
                  <span>
                    {stats.criteria} {translateUi("criteria")}
                  </span>
                </p>
              </div>
              {companyWizardContext && (
                <span
                  className={cbCn(
                    "inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                    fixed
                      ? isDarkMode
                        ? "bg-slate-800 text-slate-200"
                        : "bg-slate-200 text-slate-700"
                      : isDarkMode
                        ? "bg-emerald-950/70 text-emerald-200"
                        : "bg-emerald-100 text-emerald-800"
                  )}
                >
                  {fixed ? (
                    <>
                      <Lock className="h-3 w-3" aria-hidden /> {translateUi("Fixed")}
                    </>
                  ) : (
                    translateUi("Editable")
                  )}
                </span>
              )}
            </div>
            <button
              type="button"
              aria-pressed={selected}
              onClick={() => onUseTemplate(cycle)}
              className={cbCn(
                "mt-4 rounded-xl px-4 text-sm font-semibold shadow-sm transition-colors",
                isMobile ? "min-h-[48px] w-full py-3" : "self-end py-2.5",
                selected
                  ? isDarkMode
                    ? "border border-indigo-400 bg-slate-900 text-indigo-200 hover:bg-slate-800"
                    : "border border-indigo-600 bg-white text-indigo-800 hover:bg-indigo-50"
                  : "bg-indigo-600 text-white hover:bg-indigo-700"
              )}
            >
              {selected ? translateUi("Using this template") : translateUi("Use this template")}
            </button>
          </article>
        );
      })}
    </div>
  );
}

export function SelectedTemplateUnitsPanel({
  cycle,
  locked,
  onChangeTemplate,
  onSaveOutline,
  outlineSaveState = "idle",
  showSaveButton = false,
  isDarkMode = false,
  isMobile = false,
  activeCriteriaItems,
  liveOutline = false,
}: {
  cycle: LibraryCycle;
  locked: boolean;
  onChangeTemplate?: () => void;
  onSaveOutline?: () => void;
  outlineSaveState?: OutlinePanelSaveState;
  /** When true, show Save (draft criteria) instead of Change template. */
  showSaveButton?: boolean;
  isDarkMode?: boolean;
  isMobile?: boolean;
  /** When set, the outline only shows criteria still in the wizard (left pane). */
  activeCriteriaItems?: WizardCriteriaItem[];
  /** Outline is rebuilt from wizard rows — no template-line filtering needed. */
  liveOutline?: boolean;
}) {
  const stats = templateLibraryStats(cycle);
  const activeCriteriaCount = useMemo(() => {
    if (liveOutline || !activeCriteriaItems) return stats.criteria;
    return activeCriteriaItems.filter((c) => String(c.criteria ?? "").trim()).length;
  }, [activeCriteriaItems, liveOutline, stats.criteria]);
  const outlineItems = liveOutline ? undefined : activeCriteriaItems;
  const duration = formatCycleDurationLabel(templateDurationMonths(cycle));

  return (
    <aside
      className={cbCn(
        "flex min-h-0 h-full flex-col overflow-hidden rounded-2xl border",
        isDarkMode ? "border-slate-700 bg-slate-900/95" : "border-slate-200 bg-white"
      )}
    >
      <div className={cbCn("shrink-0 border-b px-4 py-4 sm:px-5", isDarkMode ? "border-slate-700 bg-slate-900" : "border-slate-100 bg-slate-50/90")}>
        <div className="flex items-start gap-3">
          <span
            className={cbCn(
              "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl",
              isDarkMode ? "bg-indigo-500/20 text-indigo-200" : "bg-indigo-100 text-indigo-700"
            )}
            aria-hidden
          >
            <ClipboardList className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className={cbCn("text-[11px] font-semibold uppercase tracking-wider", isDarkMode ? "text-indigo-300" : "text-indigo-700")}>
              {liveOutline || activeCriteriaItems ? translateUi("Your cycle outline") : translateUi("Selected template")}
            </p>
            <h4 className={cbCn("mt-0.5 text-sm font-semibold leading-snug break-words sm:text-base", isDarkMode ? "text-slate-50" : "text-slate-900")}>
              {cycle.name}
            </h4>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <span className={cbCn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium", isDarkMode ? "bg-slate-800 text-slate-300" : "bg-white text-slate-600 ring-1 ring-slate-200")}>
                <Clock className="h-3 w-3" aria-hidden />
                {duration}
              </span>
              <span className={cbCn("rounded-full px-2 py-0.5 text-[11px] font-medium", isDarkMode ? "bg-slate-800 text-slate-300" : "bg-white text-slate-600 ring-1 ring-slate-200")}>
                {stats.units} {translateUi("units")}
              </span>
              <span className={cbCn("rounded-full px-2 py-0.5 text-[11px] font-medium", isDarkMode ? "bg-slate-800 text-slate-300" : "bg-white text-slate-600 ring-1 ring-slate-200")}>
                {activeCriteriaCount} {translateUi("criteria")}
              </span>
              {locked && (
                <span className={cbCn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold", isDarkMode ? "bg-slate-800 text-slate-200" : "bg-slate-200 text-slate-700")}>
                  <Lock className="h-3 w-3" aria-hidden />
                  {translateUi("Fixed")}
                </span>
              )}
            </div>
          </div>
          {showSaveButton ? (
            <button
              type="button"
              onClick={() => void onSaveOutline?.()}
              disabled={outlineSaveState === "saving" || !onSaveOutline}
              className={cbCn(
                "inline-flex shrink-0 items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold transition-colors",
                isMobile && "min-h-[44px]",
                outlineSaveState === "saved"
                  ? isDarkMode
                    ? "border border-emerald-600/50 bg-emerald-950/50 text-emerald-200"
                    : "border border-emerald-300 bg-emerald-50 text-emerald-800"
                  : isDarkMode
                    ? "border border-indigo-500/40 bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-60"
                    : "border border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
              )}
            >
              {outlineSaveState === "saved" ? (
                <>
                  <CheckCircle2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  {translateUi("Saved")}
                </>
              ) : outlineSaveState === "saving" ? (
                translateUi("Saving…")
              ) : (
                translateUi("Save")
              )}
            </button>
          ) : onChangeTemplate ? (
            <button
              type="button"
              onClick={onChangeTemplate}
              className={cbCn(
                "shrink-0 rounded-xl px-3 py-2 text-xs font-semibold",
                isMobile && "min-h-[44px]",
                isDarkMode
                  ? "border border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700"
                  : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              )}
            >
              {translateUi("Change")}
            </button>
          ) : null}
        </div>
      </div>
      <TemplateUnitsCriteriaScroll
        cycle={cycle}
        isDarkMode={isDarkMode}
        isMobile={isMobile}
        activeCriteriaItems={outlineItems}
      />
    </aside>
  );
}
